import React, { useMemo, useState, useEffect, useRef } from 'react'
import { Box, Text, Input, Select, Icon, Avatar, InputGroup, InputRightElement, Flex, Button } from '@chakra-ui/react'
import { CircleFlag } from 'react-circle-flags'
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons'
import { isEmpty } from 'validator'
import { WarningIcon } from '../../../../assets/icons'
import ModalMultiSelect from '../../../../components/forms/modalForms/modalMultiSelect'
import { CustomDateInput } from '../../../../components/forms/modalForms/customDate'
import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchShortEmployeeList } from '../../../../api/workforce/workforce'
import ModalSelect from '../../../forms/modalForms/modalSelect'
import { fetchJobList } from '../../../../api/classification/classification'
import { phoneNumbers, countrySelectOptions, getCodeByCountryName } from '../../../countries/PhoneDial'

interface SelectedUser {
  id: string
  first_name: string
  last_name: string
  profile_picture: string
}

interface EditableFieldProps {
  label?: string
  isUser?: boolean
  value: string
  onSave: (newValue: string) => void
  hideLabel?: boolean
  alwaysEditable?: boolean
  isEditing?: boolean
  textStyleOnDisplay?: string
  width?: string
  mb?: string
  height?: string
  isCountrySelect?: boolean
  countryOptions?: { key: string; name: string }[]
  isSelect?: boolean
  pr?: string
  error?: string
  selectOptions?: { key: string; name: string }[]
  isMultiSelect?: boolean
  multiSelectOptions?: { key: string; name: string }[]
  isDate?: boolean
  isTextArea?: boolean
  isOptional?: boolean
  hideEditableIndicator?: boolean
  isEmployee?: boolean
  isJobProfile?: boolean
  employeePlaceholder?: string
  jobProfilePlaceholder?: string
  jobProfileEmptyOptionLabel?: string
  onEmployeeSave?: (user: SelectedUser) => void
  onJobProfileSave?: (job: { id: string; title: string }) => void
  managerDetails?: { id: string; first_name: string; last_name: string; profile_picture: string }
  isEdgeLabel?: boolean
  isDisabled?: boolean
  isNumber?: boolean
  min?: number
  max?: number
  step?: number
  // Optional status styling for display/input colors
  statusStyle?: 'warning' | 'success' | 'error'
  // Optional display text color override when not editing
  displayColor?: string
  // If true, input/select renders transparent and borderless to merge with parent container styles
  placeholder?: string
  /** Phone number with country: value is full number (e.g. +31612345678), onSave receives same */
  isPhoneNumber?: boolean
  phonePlaceholder?: string
  onClear?: () => void
}

const EditableField: React.FC<EditableFieldProps & { isGeneralScore?: boolean; isPublished?: boolean }> = ({
  label,
  value,
  onSave,
  hideLabel = false,
  textStyleOnDisplay = 'l-s',
  width = '208px',
  height = '20px',
  isCountrySelect = false,
  countryOptions = [],
  isSelect = false,
  selectOptions = [],
  isMultiSelect = false,
  multiSelectOptions = [],
  isDate = false,
  isTextArea = false,
  isOptional = false,
  hideEditableIndicator = false,
  alwaysEditable = false,
  isEditing = false,
  isEmployee = false,
  isJobProfile = false,
  employeePlaceholder,
  jobProfilePlaceholder,
  jobProfileEmptyOptionLabel,
  onEmployeeSave,
  onJobProfileSave,
  managerDetails,
  isEdgeLabel = false,
  isDisabled = false,
  isNumber = false,
  min = 0,
  max = Infinity,
  step = 1,
  statusStyle,
  displayColor,
  placeholder,
  isGeneralScore = false,
  isPublished = false,
  isPhoneNumber = false,
  phonePlaceholder,
  onClear,
}) => {
  const [newValue, setNewValue] = useState(value)
  const [isEditingState, setIsEditingState] = useState(false)
  const [validCountry, setValidCountry] = useState<{ key: string; name: string } | null>(null)
  const [error, setError] = useState('')
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [isEmployeeOpen, setIsEmployeeOpen] = useState(false)
  const [employeeSearch, setEmployeeSearch] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState<SelectedUser | null>(null)

  const [jobProfileSearch, setJobProfileSearch] = useState('')
  const [, setIsJobProfileOpen] = useState(false)
  const [selectedJobProfile, setSelectedJobProfile] = useState<{ id: string; title: string } | null>(null)

  const [isHovered, setIsHovered] = useState(false)

  // Phone: value is full number (dial_code + local). Derive country from longest matching dial_code.
  const phoneCountryList = useMemo(() => [...phoneNumbers].sort((a, b) => b.dial_code.length - a.dial_code.length), [])
  const normalizePhoneForMatch = (v: string) => {
    const raw = (v || '').trim()
    return raw.startsWith('+') ? raw : raw ? `+${raw}` : ''
  }
  const [phoneCountry, setPhoneCountry] = useState(() => {
    if (!isPhoneNumber || !value) return phoneNumbers.length > 0 ? phoneNumbers[0].code.toLowerCase() : ''
    const withPlus = normalizePhoneForMatch(value)
    const found = withPlus ? phoneCountryList.find((c) => withPlus.startsWith(c.dial_code)) : null
    return found ? found.code.toLowerCase() : (phoneNumbers[0]?.code?.toLowerCase() ?? '')
  })

  const pageSize = 10

  const isEditable = hideEditableIndicator ? false : isEditingState

  // Read certain props/locals to satisfy strict no-unused-vars linting without changing behavior
  void alwaysEditable
  void isEmployeeOpen

  const employeeQuery = useInfiniteQuery({
    queryKey: ['employeeSelect', employeeSearch, pageSize],
    queryFn: ({ pageParam }) => fetchShortEmployeeList({ search: employeeSearch, page: pageParam, pageSize }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: isEmployee && isEditable,
  })

  const JobQuery = useInfiniteQuery({
    queryKey: ['jobSelect', jobProfileSearch, pageSize],
    queryFn: ({ pageParam }) => fetchJobList({ search: jobProfileSearch, page: pageParam, pageSize }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage?.has_next ? allPages.length + 1 : undefined),
    enabled: isJobProfile && isEditable,
  })

  useEffect(() => {
    if (managerDetails?.id && isEmployee) {
      setSelectedEmployee({
        id: managerDetails.id,
        first_name: managerDetails.first_name,
        last_name: managerDetails.last_name,
        profile_picture: managerDetails.profile_picture,
      })
    }
  }, [managerDetails, isEmployee])

  const employeeList: SelectedUser[] = useMemo(() => {
    const pages = employeeQuery.data?.pages ?? []
    return pages.flatMap((p: any) => (Array.isArray(p?.data) ? p.data : []))
  }, [employeeQuery.data?.pages])

  const employeeOptions = useMemo(() => {
    const options = employeeList.map((user) => ({
      value: user.id,
      label: `${user.first_name} ${user.last_name}`,
      profilePicture: user.profile_picture,
    }))

    if (selectedEmployee && !options.some((o) => o.value === selectedEmployee.id)) {
      options.unshift({
        value: selectedEmployee.id,
        label: `${selectedEmployee.first_name} ${selectedEmployee.last_name}`,
        profilePicture: selectedEmployee.profile_picture,
      })
    }

    return options
  }, [employeeList, selectedEmployee])

  const jobList: any[] = useMemo(() => {
    const pages = JobQuery.data?.pages ?? []
    return pages.flatMap((p: any) => (Array.isArray(p?.data) ? p.data : []))
  }, [JobQuery.data?.pages])

  const jobOptions = useMemo(() => {
    const options = jobList.map((job: any) => ({
      value: String(job.id),
      label: String(job.title),
    }))

    if (jobProfileEmptyOptionLabel) {
      options.unshift({ value: '', label: jobProfileEmptyOptionLabel })
    }

    if (isOptional && !options.some((o) => o.value === '')) {
      options.unshift({ value: '', label: jobProfileEmptyOptionLabel ?? '—' })
    }

    if (selectedJobProfile && !options.some((o) => o.value === selectedJobProfile.id)) {
      const insertIndex = isOptional ? 1 : 0
      options.splice(insertIndex, 0, { value: selectedJobProfile.id, label: selectedJobProfile.title })
    }

    return options
  }, [jobList, selectedJobProfile, isOptional, jobProfileEmptyOptionLabel])

  useEffect(() => {
    setNewValue(value)
  }, [value])

  useEffect(() => {
    if (isPhoneNumber && value) {
      const withPlus = normalizePhoneForMatch(value)
      const found = withPlus ? phoneCountryList.find((c) => withPlus.startsWith(c.dial_code)) : null
      if (found) setPhoneCountry(found.code.toLowerCase())
    }
  }, [isPhoneNumber, value, phoneCountryList])

  // Only enter edit mode on click, not automatically when isEditing changes
  useEffect(() => {
    if (!isEditing) {
      setIsEditingState(false)
    }
    // Do not auto-set true here; only allow click to set true
  }, [isEditing])

  useEffect(() => {
    if (isCountrySelect) {
      const country = countryOptions.find((option) => option.name.toLowerCase() === newValue.toLowerCase())
      setValidCountry(country || null)
    }
  }, [newValue, countryOptions, isCountrySelect])

  useEffect(() => {
    // We need this handler for employee, number inputs, date inputs, and country selects
    if (!isEmployee && !isNumber && !isDate && !isJobProfile && !isPhoneNumber && !isCountrySelect) return

    function handleClickOutside(event: MouseEvent) {
      // Check if click is inside the dropdown (portal)
      const portalElements = document.querySelectorAll('[data-modal-select-dropdown], [data-date-picker-portal]')
      let clickedInPortal = false

      portalElements.forEach((el) => {
        if (el.contains(event.target as Node)) {
          clickedInPortal = true
        }
      })

      // Check if click is on number control buttons or date picker
      const isNumberControl = (event.target as Element)?.closest('[data-number-control]') !== null
      const isDatePicker = (event.target as Element)?.closest('.rs-picker-menu, .rs-calendar') !== null

      // Only close if click is not in wrapper, not in dropdown, and not on controls
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node) &&
        !clickedInPortal &&
        !isNumberControl &&
        !isDatePicker
      ) {
        setIsEmployeeOpen(false)
        setIsJobProfileOpen(false)
        setEmployeeSearch('')
        setJobProfileSearch('')
        setIsEditingState(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isEmployee, isNumber, isDate, isPhoneNumber, isCountrySelect])

  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleSave = (valueToSave: string = newValue) => {
    if (!isOptional && isEmpty(valueToSave)) {
      setError('This field cannot be empty, add N/A if optional')
    } else {
      setError('')
      if (validCountry && valueToSave === validCountry.name) onSave(validCountry.name)
      else onSave(valueToSave)
      setIsEditingState(false)
    }
  }

  const handleClick = () => {
    if (isDisabled) return
    setIsEditingState(true)
  }

  const handleBlur = (
    blurValue?: string | React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const valueToSave = typeof blurValue === 'string' ? blurValue : (blurValue?.target?.value ?? newValue)

    if (valueToSave === value) {
      setIsEditingState(false)
      return
    }
    if (!isOptional && isEmpty(valueToSave)) {
      setError('This field cannot be empty')
    } else if (isOptional && isEmpty(valueToSave)) {
      setError('Add N/A if empty')
    } else {
      if (valueToSave !== newValue) {
        setNewValue(valueToSave)
      }
      handleSave(valueToSave)
    }
  }

  // Add these functions for number increment/decrement
  const increment = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (isNumber) {
      const currentValue = Number(newValue) || 0
      const newVal = Math.min(currentValue + step, max)
      setNewValue(String(newVal))

      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }

      // Set a new timeout to save the value after 500ms of inactivity
      updateTimeoutRef.current = setTimeout(() => {
        onSave(String(newVal))
      }, 1500)
    }
  }

  const decrement = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    if (isNumber) {
      const currentValue = Number(newValue) || 0
      const newVal = Math.max(currentValue - step, min)
      setNewValue(String(newVal))

      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }

      // Set a new timeout to save the value after 500ms of inactivity
      updateTimeoutRef.current = setTimeout(() => {
        onSave(String(newVal))
      }, 1500)
    }
  }

  // Handle number input change
  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numValue = e.target.value === '' ? '' : Number(e.target.value)
    if (numValue === '' || (!isNaN(numValue) && numValue >= min && numValue <= max)) {
      setNewValue(e.target.value)

      // Clear any existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }

      // Set a new timeout to save the value after 500ms of inactivity
      updateTimeoutRef.current = setTimeout(() => {
        onSave(e.target.value)
      }, 500)
    }
  }

  return (
    <Box ref={wrapperRef} width={width} minH={height} height="auto" onClick={handleClick}>
      {!hideLabel && label && (
        <Text textStyle="l-xs" color="solid.500">
          {label}
        </Text>
      )}
      {isEditable ? (
        isGeneralScore ? (
          <Flex align="center" justify="center" gap="8px">
            {Array.from({ length: 5 }).map((_, idx) => {
              const filled = Number(value) >= (idx + 1) * 20
              return (
                <Button
                  key={idx}
                  variant="ghost"
                  minWidth="20px"
                  maxWidth="20px"
                  height="20px"
                  width="20px"
                  borderRadius="full"
                  borderWidth="1px"
                  borderColor={filled ? 'success.100' : 'solid.100'}
                  bg={filled ? 'success.100' : 'white'}
                  _hover={isDisabled || isPublished ? {} : { bg: 'success.100' }}
                  p={0}
                  onClick={() => {
                    if (isDisabled || isPublished) return
                    const newValue = (idx + 1) * 20
                    if (Number(value) !== newValue) {
                      onSave(String(newValue))
                    }
                  }}
                  isDisabled={isDisabled || isPublished}
                  sx={{
                    opacity: 1,
                    color: 'inherit',
                    pointerEvents: isDisabled || isPublished ? 'none' : 'auto',
                  }}
                />
              )
            })}
          </Flex>
        ) : isEmployee ? (
          <Box>
            <ModalSelect
              name="employee"
              value={selectedEmployee?.id || ''}
              options={employeeOptions}
              onChange={(e) => {
                const userId = e.target.value
                if (!userId) {
                  setSelectedEmployee(null)
                  onSave('')
                  return
                }
                const user = employeeList.find((u: SelectedUser) => u.id === userId)
                if (user) {
                  setSelectedEmployee(user)
                  onEmployeeSave ? onEmployeeSave(user) : onSave(`${user.id}`)
                }
              }}
              placeholder={employeePlaceholder || 'Select a manager'}
              hasProfilePicture={true}
              isDisabled={isDisabled}
              onSearch={(term) => {
                setEmployeeSearch(term)
              }}
              isLoading={employeeQuery.isLoading}
              onLoadMore={() => employeeQuery.fetchNextPage()}
              hasMore={!!employeeQuery.hasNextPage}
              isLoadingMore={employeeQuery.isFetchingNextPage}
              isTop={true}
            />
          </Box>
        ) : isJobProfile ? (
          <Box>
            <ModalSelect
              name="job profile"
              value={selectedJobProfile?.id || ''}
              options={jobOptions}
              onChange={(e) => {
                const jobId = e.target.value
                // Handle N/A selection (empty value)
                if (jobId === '') {
                  setSelectedJobProfile(null)
                  onSave('')
                  setIsJobProfileOpen(false)
                  setIsEditingState(false)
                  return
                }
                // Find the selected job profile
                const job = jobList.find((j: any) => String(j.id) === String(jobId))
                if (job) {
                  setSelectedJobProfile(job)
                  if (onJobProfileSave) {
                    onJobProfileSave(job)
                  } else {
                    onSave(`${job.id}`)
                  }
                  setIsJobProfileOpen(false)
                  setIsEditingState(false)
                }
              }}
              placeholder={jobProfilePlaceholder || 'Select a job profile'}
              hasProfilePicture={false}
              isDisabled={isDisabled}
              onSearch={(term) => {
                setJobProfileSearch(term)
              }}
              isLoading={JobQuery.isLoading}
              onLoadMore={() => JobQuery.fetchNextPage()}
              hasMore={!!JobQuery.hasNextPage}
              isLoadingMore={JobQuery.isFetchingNextPage}
              isTop={true}
            />
          </Box>
        ) : isNumber ? (
          <InputGroup height="40px" w="auto" minW="70px">
            {' '}
            {/* Changed from fixed 10px height */}
            <Input
              type="number"
              value={newValue}
              onChange={handleNumberChange}
              onBlur={handleBlur}
              textAlign="center"
              py={2}
              pr={4} /* Increased right padding to accommodate buttons */
              pl={2}
              style={{
                width: `${Math.max(69, (String(newValue).length + 2) * 10)}px`,
                transition: 'none' /* Removed animation to prevent popping */,
              }}
              min={min}
              max={max}
              autoFocus
              height="70%"
              onClick={(e) => e.stopPropagation()}
            />
            <InputRightElement display="flex" h="70%">
              {' '}
              {/* Fixed height */}
              <Flex direction="column" justifyContent="center">
                {' '}
                {/* Changed to center */}
                <Button
                  variant="hoverEffect"
                  onMouseDown={(e) => {
                    // Use onMouseDown instead of onClick
                    e.preventDefault()
                    e.stopPropagation()
                    increment()
                  }}
                  size="xxs"
                  p={0}
                  data-number-control="true"
                  isDisabled={isDisabled || Number(newValue) >= max}
                >
                  <Icon boxSize="16px" color="solid.400" fill="none">
                    <ChevronUpIcon />
                  </Icon>
                </Button>
                <Button
                  variant="hoverEffect"
                  onMouseDown={(e) => {
                    // Use onMouseDown instead of onClick
                    e.preventDefault()
                    e.stopPropagation()
                    decrement()
                  }}
                  size="xxs"
                  p={0}
                  data-number-control="true"
                  isDisabled={isDisabled || Number(newValue) <= min}
                >
                  <Icon boxSize="16px" color="solid.400" fill="none">
                    <ChevronDownIcon />
                  </Icon>
                </Button>
              </Flex>
            </InputRightElement>
          </InputGroup>
        ) : isDate ? (
          <Box position="relative" zIndex={1} width="100%">
            <CustomDateInput
              className="date-input"
              ref={undefined}
              onBlur={handleBlur}
              defaultValue={(() => {
                if (!newValue || newValue === 'N/A') return null
                const parts = newValue.split('/')
                if (parts.length === 3) {
                  const [day, month, year] = parts
                  return new Date(Number(year), Number(month) - 1, Number(day))
                }
                const parsed = new Date(newValue)
                return isNaN(parsed.getTime()) ? null : parsed
              })()}
              onChange={(selectedDate) => {
                if (selectedDate) {
                  // Format date as DD-MM-YYYY for display and saving
                  const formattedDate = `${String(selectedDate.getDate()).padStart(2, '0')}/${String(selectedDate.getMonth() + 1).padStart(2, '0')}/${selectedDate.getFullYear()}`
                  setNewValue(formattedDate)

                  // Clear any existing timeout
                  if (updateTimeoutRef.current) {
                    clearTimeout(updateTimeoutRef.current)
                  }

                  // Set a new timeout to save the value
                  updateTimeoutRef.current = setTimeout(() => {
                    onSave(formattedDate)
                  }, 500)
                } else if (isOptional && onClear) {
                  setNewValue('')
                  onClear()
                }
              }}
            />
          </Box>
        ) : isPhoneNumber ? (
          <Flex
            position="relative"
            borderRadius="8px"
            overflow="hidden"
            alignItems="center"
            borderWidth="1px"
            borderColor="solid.100"
            width={width}
            minH="40px"
            onClick={(e) => e.stopPropagation()}
          >
            <Select
              variant="countries"
              borderRadius="8px 0 0 8px"
              width="88px"
              minW="88px"
              value={phoneCountry}
              onChange={(e) => {
                const val = e.target.value
                setPhoneCountry(val)
                const selected = phoneNumbers.find((c) => c.code.toLowerCase() === val)
                const dial = phoneNumbers.find((c) => c.code.toLowerCase() === phoneCountry)?.dial_code ?? ''
                const local = newValue.startsWith(dial) ? newValue.slice(dial.length) : newValue.replace(/^\+\d*/, '')
                if (selected) setNewValue(selected.dial_code + local)
              }}
              sx={{
                textIndent: '-9999px',
                lineHeight: '0',
                fontSize: '0',
                '& > option': { textIndent: '0px', paddingLeft: '40px', lineHeight: 'normal', fontSize: '12px' },
              }}
            >
              {phoneNumbers.map((c, i) => (
                <option key={i} value={c.code.toLowerCase()}>
                  {c.name}
                </option>
              ))}
            </Select>
            <Box position="absolute" left="8px" top="50%" transform="translateY(-50%)" pointerEvents="none" zIndex={1}>
              <CircleFlag countryCode={phoneCountry} style={{ width: 20, height: 20, borderRadius: 4 }} />
            </Box>
            <Text
              position="absolute"
              left="32px"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              textStyle="b-s"
              color="solid.900"
              zIndex={1}
            >
              {phoneNumbers.find((c) => c.code.toLowerCase() === phoneCountry)?.dial_code ?? ''}
            </Text>
            <Input
              flex={1}
              borderRadius="0 8px 8px 0"
              borderLeftWidth="1px"
              borderLeftColor="solid.100"
              value={(() => {
                const dial = phoneNumbers.find((c) => c.code.toLowerCase() === phoneCountry)?.dial_code ?? ''
                return newValue.startsWith(dial) ? newValue.slice(dial.length) : newValue.replace(/^\+\d*/, '')
              })()}
              onChange={(e) => {
                const dial = phoneNumbers.find((c) => c.code.toLowerCase() === phoneCountry)?.dial_code ?? ''
                setNewValue(dial + e.target.value)
              }}
              onBlur={handleBlur}
              placeholder={
                phoneNumbers.find((c) => c.code.toUpperCase() === phoneCountry.toUpperCase())?.example
                  ? `e.g., ${phoneNumbers.find((c) => c.code.toUpperCase() === phoneCountry.toUpperCase())?.example}`
                  : phonePlaceholder || 'Phone number'
              }
            />
          </Flex>
        ) : isCountrySelect ? (
          <Box width={width} minW={0}>
            <ModalSelect
              name="country"
              value={getCodeByCountryName(newValue)?.toUpperCase() || newValue}
              options={countrySelectOptions}
              onChange={(e) => {
                const code = e.target.value as string
                const opt = countrySelectOptions.find((o) => o.value === code)
                const name = opt?.label || code
                setNewValue(name)
                handleBlur(name)
              }}
              showCountryFlag
              placeholder=""
            />
          </Box>
        ) : isSelect ? (
          <Box width={width} minW={0}>
            <Select
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              onBlur={(e) => handleBlur(e.target.value)}
              autoFocus
              variant="editable"
              width="100%"
              maxW="100%"
              sx={{
                '&': { width: '100%', maxWidth: '100%' },
                '& > select': { width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
              }}
            >
              {selectOptions.map((option) => (
                <option key={option.key} value={option.name}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Box>
        ) : isMultiSelect ? (
          <ModalMultiSelect
            label={label}
            options={multiSelectOptions}
            selectedValues={multiSelectOptions.filter((option) => newValue.includes(option.name))}
            onChange={(selectedList) => setNewValue(selectedList.map((opt) => opt.name).join(', '))}
          />
        ) : isTextArea ? (
          <Input
            as="textarea"
            value={newValue}
            variant="editable"
            placeholder={placeholder}
            onChange={(e) => {
              setNewValue(e.target.value)
              // Auto-expand textarea height
              if (e.target) {
                e.target.style.height = 'auto'
                e.target.style.height = `${e.target.scrollHeight}px`
              }
            }}
            onBlur={handleBlur}
            autoFocus
            width="100%"
            minH="46px"
            resize="none"
            overflow="hidden"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            ref={(el: HTMLTextAreaElement | null) => {
              if (el) {
                el.style.height = 'auto'
                el.style.height = `${el.scrollHeight}px`
              }
            }}
          />
        ) : isEdgeLabel ? (
          <Input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleBlur()
              }
            }}
            autoFocus
            height="100%"
            fontSize="14px"
            borderRadius="20px"
            textAlign="center"
            border="none"
            _focus={{
              borderColor: 'none',
              boxShadow: 'none',
              outline: 'none',
            }}
          />
        ) : (
          <Input
            value={newValue}
            variant="editable"
            onChange={(e) => setNewValue(e.target.value)}
            onBlur={handleBlur}
            autoFocus
            width="100%"
            height="100%"
            p={0}
          />
        )
      ) : (
        <>
          {isEmployee ? (
            <Flex
              align="flex-start"
              gap="8px"
              wrap="wrap"
              position="relative"
              onMouseEnter={() => {
                if (onClear && managerDetails?.id && !hideEditableIndicator) setIsHovered(true)
              }}
              onMouseLeave={() => {
                if (onClear) setIsHovered(false)
              }}
            >
              {managerDetails?.id ? (
                <>
                  <Avatar
                    size="xs"
                    src={managerDetails.profile_picture}
                    name={`${managerDetails.first_name} ${managerDetails.last_name}`}
                  />
                  <Text textStyle={textStyleOnDisplay} color="solid.900">
                    {managerDetails.first_name} {managerDetails.last_name}
                  </Text>
                  {onClear && isHovered && (
                    <Box
                      as="button"
                      aria-label={`Clear ${managerDetails.first_name} ${managerDetails.last_name}`}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                        onClear()
                      }}
                      color="solid.400"
                      _hover={{ color: 'error.100' }}
                      lineHeight={1}
                      fontSize="16px"
                      ml={1}
                    >
                      ×
                    </Box>
                  )}
                </>
              ) : (
                <Text textStyle={textStyleOnDisplay} color="solid.900">
                  {employeePlaceholder || 'N/A'}
                </Text>
              )}
            </Flex>
          ) : (
            <Flex
              align="flex-start"
              gap="8px"
              wrap="wrap"
              position="relative"
              onMouseEnter={() => {
                if (onClear && !hideEditableIndicator) setIsHovered(true)
              }}
              onMouseLeave={() => {
                if (onClear) setIsHovered(false)
              }}
            >
              {isCountrySelect && validCountry && validCountry.key && (
                <CircleFlag countryCode={validCountry.key} style={{ width: '24px', height: '24px' }} />
              )}
              {isPhoneNumber &&
                newValue &&
                (() => {
                  const withPlus = normalizePhoneForMatch(newValue)
                  const found = withPlus ? phoneCountryList.find((c) => withPlus.startsWith(c.dial_code)) : null
                  return found ? (
                    <CircleFlag countryCode={found.code.toLowerCase()} style={{ width: '24px', height: '24px' }} />
                  ) : null
                })()}
              <Text
                textStyle={textStyleOnDisplay}
                color={
                  statusStyle === 'warning'
                    ? 'warning.200'
                    : statusStyle === 'success'
                      ? 'success.300'
                      : statusStyle === 'error'
                        ? 'error.100'
                        : displayColor || 'solid.900'
                }
              >
                {newValue || placeholder || 'N/A'}
              </Text>
              {onClear && isHovered && newValue && newValue !== 'N/A' && (
                <Box
                  as="button"
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation()
                    onClear()
                  }}
                  color="solid.400"
                  _hover={{ color: 'error.100' }}
                  lineHeight={1}
                  fontSize="16px"
                  ml={1}
                >
                  ×
                </Box>
              )}
            </Flex>
          )}
        </>
      )}
      {error && (
        <Box textStyle="b-xs" color="error.100" w="100%" mt={1}>
          <Text>
            <Icon w="16px" h="16px" viewBox="0 0 24 24" fill="none" color="error.100" mt="2px">
              <WarningIcon />
            </Icon>
            {error}
          </Text>
        </Box>
      )}
    </Box>
  )
}

export default EditableField
